import { createRoute, type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { lazy, type ReactElement, Suspense, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { rootRoute } from "../../root-route";
import { rechargerUneFois } from "../rechargement";
import { estUnLundi } from "../semaine-dates";
import { lundiDe } from "./dates";
import { A_POSTER_PATH, CHARGEMENT, INDISPONIBLE, PLANNING_PATH } from "./textes";

/**
 * Les deux routes du planning. Les ecrans sont livres a part (ils portent l'affiche, ses polices et l'apercu) : le
 * paquet d'entree ne les charge qu'a l'ouverture de la page, comme la diffusion et l'ecran En direct. `?lundi=` est la
 * semaine montree ; une valeur qui n'est pas un lundi du calendrier est ignoree et la page montre la semaine en cours.
 */

class ChargementPlanningEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement de l'ecran du planning echoue", { cause });
    this.name = "ChargementPlanningEchoue";
  }
}

const SemainePage = lazy(() =>
  import("./SemainePage").then(
    (module) => ({ default: module.SemainePage }),
    (cause: unknown) => {
      throw new ChargementPlanningEchoue(cause);
    },
  ),
);

const KitPage = lazy(() =>
  import("./KitPage").then(
    (module) => ({ default: module.KitPage }),
    (cause: unknown) => {
      throw new ChargementPlanningEchoue(cause);
    },
  ),
);

const RECHARGER_LA_PAGE = (): void => window.location.reload();

/** Le fichier de l'ecran n'est pas arrive : une phrase, un rechargement tout seul (une fois) et le bouton. */
export function PlanningIndisponible({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <section role="alert" className="flex flex-col items-start gap-3">
      <p>{INDISPONIBLE}</p>
      <Button onClick={recharger}>Recharger la page</Button>
    </section>
  );
}

function ErreurDuPlanning({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementPlanningEchoue)) throw error;
  return <PlanningIndisponible />;
}

const validerRecherche = (search: Record<string, unknown>): { lundi?: string | undefined } => ({
  lundi: estUnLundi(search.lundi) ? search.lundi : undefined,
});

/** La semaine montree : celle de l'adresse, sinon celle de Paris aujourd'hui. */
const semaineMontree = (lundi: string | undefined): string => lundi ?? lundiDe(new Date());

const attente = (
  <p role="status" className="text-sm text-muted-foreground">
    {CHARGEMENT}
  </p>
);

export const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: PLANNING_PATH,
  validateSearch: validerRecherche,
  component: PlanningRoute,
  errorComponent: ErreurDuPlanning,
});

function PlanningRoute(): ReactElement {
  const { lundi } = planningRoute.useSearch();
  const navigate = planningRoute.useNavigate();
  const router = useRouter();
  const montree = semaineMontree(lundi);
  return (
    <Suspense fallback={attente}>
      <SemainePage
        lundi={montree}
        onChoisirSemaine={(choisie) => void navigate({ search: { lundi: choisie } })}
        onPublie={(publiee) => router.history.push(`${A_POSTER_PATH}?lundi=${publiee}`)}
      />
    </Suspense>
  );
}

export const aPosterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: A_POSTER_PATH,
  validateSearch: validerRecherche,
  component: APosterRoute,
  errorComponent: ErreurDuPlanning,
});

function APosterRoute(): ReactElement {
  const { lundi } = aPosterRoute.useSearch();
  return (
    <Suspense fallback={attente}>
      <KitPage lundi={semaineMontree(lundi)} />
    </Suspense>
  );
}
